import { useSelector } from "react-redux";
import { Grid2 as Grid } from "@mui/material";
import StatisticsCard from "../../components/cards/StatisticsCard";
import PageWrapper from "../../components/page/PageWrapper";
import PageHeader from "../../components/common/PageHeader";
import FiltersContainer from "../../components/filters/FiltersContainer";
import FromDateFilter from "../../components/filters/FromDateFilter";
import ToDateFilter from "../../components/filters/ToDateFilter";
import ErrorState from "../../components/common/ErrorState";
import useFilterParams from "../../hooks/useFilterParams";
import { BiUser } from "react-icons/bi";
import { GrUserAdmin } from "react-icons/gr";
import {
  PiBuildingOffice,
  PiMoneyWavyThin,
  PiStackLight,
} from "react-icons/pi";
import fetching from "../../helpers/fetching/fetching";
import { useQuery } from "@tanstack/react-query";
import type { UserState, Stats } from "../../types/types";
import { CHART_COLORS } from "../../theme/MuiTheme";

const statCards = [
  { title: "المستخدمين", key: "total_users" as const, Icon: BiUser, type: "count" as const },
  { title: "المضيفين", key: "total_hosts" as const, Icon: GrUserAdmin, type: "count" as const },
  { title: "الإقامات", key: "total_offers" as const, Icon: PiBuildingOffice, type: "count" as const },
  { title: "الحجوزات", key: "total_reservations" as const, Icon: PiStackLight, type: "count" as const },
  { title: "قيمة الحجوزات", key: "total_income" as const, Icon: PiMoneyWavyThin, type: "price" as const },
  { title: "الايرادات", key: "total_app_profit" as const, Icon: PiMoneyWavyThin, type: "price" as const },
];

const Home = () => {
  const userToken = useSelector((state: UserState) => state.user.userToken);
  const { from, to } = useFilterParams();

  const getStatistics = () => {
    let endpoint = "/statistics?";
    if (to) endpoint += "to=" + to;
    if (to && from) endpoint += "&";
    if (from) endpoint += "from=" + from;
    return fetching(endpoint, userToken)
      .then((res) => res.json())
      .then((data) => {
        if (data?.status && data?.status !== 200) {
          throw data;
        }
        return data;
      });
  };

  const {
    data: statistics,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery<Stats, any>({
    queryKey: ["stats", from, to],
    queryFn: getStatistics,
  });

  return (
    <PageWrapper>
      <PageHeader title="الرئيسية" description="نظرة عامة على إحصائيات المنصة" />
      <FiltersContainer>
        <FromDateFilter />
        <ToDateFilter />
      </FiltersContainer>

      {isError ? (
        <ErrorState
          message={error?.message || "حدث خطأ أثناء تحميل الإحصائيات"}
          onRetry={() => refetch()}
        />
      ) : (
        <Grid container spacing={2}>
          {statCards.map((card, index) => (
            <Grid key={card.key} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
              <StatisticsCard
                title={card.title}
                value={statistics?.[card.key] || 0}
                Icon={card.Icon}
                color={CHART_COLORS[index % CHART_COLORS.length]}
                loading={loading}
                type={card.type}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </PageWrapper>
  );
};

export default Home;
